import { useEffect, useEffectEvent, useState } from "react";
import { log } from "@connect/observability";
import { isInFlight } from "./use-onboarding-orchestrator";
import type {
  UseVerifyingPollParams,
  UseVerifyingPollResult,
  VerifyingPollTiming,
} from "./use-verifying-poll.types";

export const VERIFYING_POLL_INITIAL_INTERVAL_MS = 2_000;
export const VERIFYING_POLL_MAX_INTERVAL_MS = 10_000;
export const VERIFYING_POLL_TIMEOUT_MS = 60_000;

interface PollOutcome {
  failed?: boolean;
  key: string;
  result?: UseVerifyingPollResult["result"];
  timedOut: boolean;
}

const POLL_DEADLINE = Symbol("poll-deadline");

const defaultSchedule: NonNullable<VerifyingPollTiming["schedule"]> = (callback, delayMs) => {
  const timeout = setTimeout(callback, delayMs);
  return () => clearTimeout(timeout);
};

export function useVerifyingPoll({
  customerId,
  enabled,
  fetchKyc,
  onTimeout,
  reportKycError,
  syncKyc,
  timing,
}: UseVerifyingPollParams): UseVerifyingPollResult {
  const [outcome, setOutcome] = useState<PollOutcome>({ key: "", timedOut: false });
  const [run, setRun] = useState(0);
  const key = `${customerId ?? ""}:${run}`;
  const result = outcome.key === key ? outcome.result : undefined;
  const failed = outcome.key === key && outcome.failed;
  const timedOut = outcome.key === key && outcome.timedOut;
  const stopped = result !== undefined && (!isInFlight(result.status) || Boolean(result.challenge));
  const polling = Boolean(enabled && customerId && !failed && !timedOut && !stopped);

  const handleTimeout = useEffectEvent(() => onTimeout?.());

  useEffect(() => {
    if (!enabled || !customerId) {
      return;
    }

    let cancelled = false;
    let cancelScheduled: (() => void) | undefined;
    let cancelDeadline: (() => void) | undefined;
    const abortController = new AbortController();
    let intervalMs = timing?.initialIntervalMs ?? VERIFYING_POLL_INITIAL_INTERVAL_MS;
    const maxIntervalMs = timing?.maxIntervalMs ?? VERIFYING_POLL_MAX_INTERVAL_MS;
    const timeoutMs = timing?.timeoutMs ?? VERIFYING_POLL_TIMEOUT_MS;
    const now = timing?.now ?? Date.now;
    const schedule = timing?.schedule ?? defaultSchedule;
    const startedAt = now();

    const finishTimeout = () => {
      if (cancelled) {
        return;
      }

      log.warn("connect.onboarding.kyc_poll_failed", { reason: "timeout" });

      setOutcome({ key, timedOut: true });

      handleTimeout();
    };

    const poll = async () => {
      if (cancelled) {
        return;
      }
      if (now() - startedAt >= timeoutMs) {
        finishTimeout();
        return;
      }

      const remainingMs = timeoutMs - (now() - startedAt);
      const deadline = new Promise<typeof POLL_DEADLINE>((resolve) => {
        cancelDeadline = schedule(() => {
          abortController.abort();
          resolve(POLL_DEADLINE);
        }, remainingMs);
      });
      const next = await Promise.race([fetchKyc(abortController.signal), deadline]);
      cancelDeadline?.();
      cancelDeadline = undefined;
      if (cancelled) {
        return;
      }
      if (next === POLL_DEADLINE || now() - startedAt >= timeoutMs) {
        finishTimeout();
        return;
      }
      if (!next.ok) {
        if (next.error.kind !== "transient") {
          reportKycError(next.error);
          setOutcome({ key, failed: true, timedOut: false });
          return;
        }
      } else {
        const snapshot = syncKyc(next.kyc);
        setOutcome({ key, result: snapshot, timedOut: false });
        if (!isInFlight(snapshot.status) || snapshot.challenge) {
          return;
        }
      }

      const delayMs = Math.min(intervalMs, timeoutMs - (now() - startedAt));
      intervalMs = Math.min(intervalMs * 2, maxIntervalMs);
      cancelScheduled = schedule(() => {
        cancelScheduled = undefined;
        void poll();
      }, delayMs);
    };

    void poll();

    return () => {
      cancelled = true;
      abortController.abort();
      cancelScheduled?.();
      cancelDeadline?.();
    };
  }, [customerId, enabled, fetchKyc, key, reportKycError, run, syncKyc, timing]);

  return {
    result,
    polling,
    timedOut,
    restart: () => setRun((current) => current + 1),
  };
}
